/**
 * Centralized definitions of the role strings used across the app.
 *
 * Roles are stored as plain strings on `UserProfile.roles`. Treating
 * `super_admin` as just another role keeps the model simple — there is no
 * separate `superAdmin: boolean` field.
 *
 * Ported from Captain's Log (which used a separate boolean for
 * super_admin); collapsed into a single roles array per Tier 2 design.
 */

export const SUPER_ADMIN_ROLE = "super_admin";

export interface RoleDefinition {
  value: string;
  label: string;
  description: string;
}

/**
 * Roles editable from the Edit User Roles modal. `super_admin` is
 * intentionally not in this list — granting it must be done out-of-band
 * (e.g. directly in Firestore) to avoid privilege escalation.
 */
export const ASSIGNABLE_ROLES: RoleDefinition[] = [
  {
    value: "manage_users",
    label: "Manage Users",
    description: "Can view all users and enable or disable accounts.",
  },
  {
    value: "manage_roles",
    label: "Manage Roles",
    description: "Can edit other users' role assignments.",
  },
  {
    value: "manage_entities",
    label: "Manage Entities",
    description: "Can create, edit, and delete entries in the entity library.",
  },
  {
    value: "view_error_details",
    label: "View Error Details",
    description: "Can see error toasts with full stack traces in the app.",
  },
];

/**
 * Display labels for any role string, including ones not in the assignable
 * list (like `super_admin`). Used by the Users page to render badges.
 */
export const ROLE_LABELS: Record<string, string> = {
  [SUPER_ADMIN_ROLE]: "Super Admin",
  manage_users: "Manage Users",
  manage_roles: "Manage Roles",
  manage_entities: "Manage Entities",
  view_error_details: "View Error Details",
};

export function isSuperAdmin(roles: string[] | undefined | null): boolean {
  if (!roles) return false;
  return roles.includes(SUPER_ADMIN_ROLE);
}
